/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import {
  Grid,
  Divider,
  Box,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { css, StyleSheet } from "aphrodite";
import { Helmet } from "react-helmet";
import Toast from "../../../../components/common/Toast";
import ChangeToNewFriendForm from "./ChangeToNewFriendForm";
import OrganizationStructure from "../../../../components/common/OrganizationStructure/OrganizationStructure";
import FriendsApi from "../../../../services/api/friend";
import {
  CHANGE_FRIEND_ENTRY_FIELDS,
  NEW_ENTRY_FIELDS,
} from "../../../../contants/formFields";
import { handleFriendsValidation } from "../../../../utils";
import Button from "../../../../components/common/Button";
import { debounce } from "../../../../utils/helper";
import { ROUTES } from "../../../../utils/constants";
import { CommonStyles } from "../../../../components/common/Button/commonButtonStyle";
import { OS_KEYS } from "../../../../contants";
import { selectAttendanceMeetingType } from "../../../../redux/selectors/attendance";

const INTRODUCER_MAPPER = {
  introducedByEmail: "introducedEmail",
  introducedByName: "introducedByName",
  introducedByMobileNumber: "introducedByMobileNumber",
};

const prepareInitialValue = (intro) => {
  const names = Object.values(CHANGE_FRIEND_ENTRY_FIELDS);
  return names.reduce((output, n) => {
    const name = INTRODUCER_MAPPER[n] || n;
    return Object.assign(output, { [name]: intro[name] || "" });
  }, {});
};

function ChangeToNewFriend() {
  const TITLE = "Change to New Friend - BSG";
  const [, setsuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(null);
  const [openDialog, setOpenDialog] = useState(false); // 🔴 For confirm alert
  const [pendingValues, setPendingValues] = useState(null); // store form values for later

  const navigate = useNavigate();
  const onBack = () => navigate(-1);
  const location = useLocation();
  const defaultMeetingType = location.state?.defaultMeetingType;
  const meetingTypeFromRedux = useSelector(selectAttendanceMeetingType);
  const locationState = location.state || {};
  console.log("state:", defaultMeetingType, meetingTypeFromRedux);

  const [finalMeetingType] = React.useState(
    locationState.defaultMeetingType ?? meetingTypeFromRedux
  );

  console.log("finalMeetingType:", finalMeetingType);

  const selectedFromRedux = useSelector(
    (state) => state.view_friends.selectedIntroductionFromSearch
  );

  const selectedIntroduction =
    location.state?.selectedIntroduction || selectedFromRedux || {};

  const introductionFromSearch = { ...selectedIntroduction };
  delete introductionFromSearch.createdAt;
  delete introductionFromSearch.updatedAt;

  const { id } = useParams();
  const initialFormValues = prepareInitialValue(selectedIntroduction);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const postData = async (values) => {
    try {
      setIsLoading(true);

      const isGoshoOrDiscussion =
        finalMeetingType === "GOSHO_MEETING" ||
        finalMeetingType === "DISCUSSION_MEETING";

      const REGION_MEETING_TYPES = new Set([
        "CUBS_MEETING",
        "YP_MEETING",
        "YL_MEETING",
        "SD_MEETING",
      ]);

      const shouldSkipOtherMeetingType =
        isGoshoOrDiscussion || REGION_MEETING_TYPES.has(finalMeetingType);

      const payload = {
        ...values,

        // ✅ meetingType only if present
        ...(finalMeetingType ? { meetingType: finalMeetingType } : {}),

        // ✅ otherMeetingType only when required
        ...(finalMeetingType && !shouldSkipOtherMeetingType
          ? { otherMeetingType: [finalMeetingType] }
          : {}),
      };

      console.log("Payload:", payload); // Debug karne ke liye

      const response = await FriendsApi.changeToNewFriend({
        data: payload,
        introductionId: id,
      });

      setIsLoading(false);

      if (response.data) {
        setShowToast({
          message: "Form submitted successfully",
          severity: "success",
        });
        setsuccess(true);
        setTimeout(() => {
          navigate(`/${ROUTES.NEW_FRIEND_SEARCH.path}`);
        }, 2000);
      }
    } catch (err) {
      setShowToast({ message: err.message, severity: "error" });
      setIsLoading(false);
      Sentry.captureException(err);
    }
  };

  const Style = StyleSheet.create({
    mainBoxStyle: {
      display: "flex",
      gap: "30px",
      "@media (max-width: 768px)": {
        flexDirection: "column",
        padding: "0px 24px",
      },
    },
  });

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>
      {/* {showIntroMsg && (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#E0F7FA",
            color: "#ff0000ff",
            marginBottom: "20px",
            borderRadius: "4px",
            textAlign: "center",
            fontWeight: 700,
            fontSize: "18px",
          }}
        >
          ⚠️Attention: Kindly note that by accepting this introduction you will
          register a New Friend and that it would be counted towards this
          month’s Discussion Meeting attendance. Please proceed only if certain.
        </Box>
      )} */}

      <Grid container>
        <Formik
          initialValues={{
            initialFormValues,
            ...introductionFromSearch,
            [NEW_ENTRY_FIELDS.COUNTRY]: selectedIntroduction.nationalityId,
            [NEW_ENTRY_FIELDS.INTRODUCED_BY_NAME]:
              selectedIntroduction.introducedByName,
            [NEW_ENTRY_FIELDS.INTRODUCED_BY_MOBILE_NUMBER]:
              selectedIntroduction.introducedByMobileNumber,
            [NEW_ENTRY_FIELDS.INTRODUCED_BY_EMAIL]:
              selectedIntroduction.introducedByEmail,
            [NEW_ENTRY_FIELDS.STATE_NAME]: selectedIntroduction.stateName || "",
            [NEW_ENTRY_FIELDS.CITY_NAME]: selectedIntroduction.cityName || "",
          }}
          onSubmit={debounce((values) => {
            const result = handleFriendsValidation(NEW_ENTRY_FIELDS, values);
            if (result && Object.keys(result).length) {
              setShowToast(result);
              return;
            }
            // 🔴 Instead of directly submitting, open dialog
            setPendingValues(values);
            setOpenDialog(true);
          })}
        >
          {({ values, errors, handleChange, handleSubmit, setFieldValue }) => (
            <>
              <Box className={css(Style.mainBoxStyle)}>
                <Grid item md={8} sm={12}>
                  <ChangeToNewFriendForm
                    values={values}
                    handleChange={handleChange}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    formValues={selectedIntroduction}
                  />
                </Grid>

                <Grid item md={4} sm={12}>
                  <OrganizationStructure
                    values={values}
                    handleChange={handleChange}
                    errors={errors}
                    handleSubmit={handleSubmit}
                    setFieldValue={setFieldValue}
                    isLoading={isLoading}
                    showZoneLocator={false}
                    disabledOSKeys={OS_KEYS}
                    showBlock
                  />
                </Grid>
              </Box>
              <Divider
                sx={{
                  display: "flex",
                  width: "100%",
                  paddingTop: "1%",
                  marginBottom: "30px",
                }}
              />
              {handleSubmit && (
                <div
                  style={{
                    display: "flex",
                    gap: 5,
                    paddingLeft: isMobile ? "2rem" : "0px",
                    minHeight: isMobile ? "190px" : "0px",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    isLoading={isLoading}
                    style={CommonStyles.buttonStyle}
                    label="Submit"
                    type="submit"
                  />
                  <Button
                    variant="outlined"
                    onClick={onBack}
                    label="Cancel"
                    style={{
                      ...CommonStyles.buttonStyle,
                      marginLeft: "20px",
                    }}
                  />
                </div>
              )}
            </>
          )}
        </Formik>
      </Grid>

      {/* 🔴 Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            minWidth: "400px",
            maxWidth: "500px",
            padding: "24px",
            textAlign: "center",
          },
        }}
      >
        {/* Warning Icon */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              border: "3px solid #f39c12",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              backgroundColor: "white",
            }}
          >
            <Typography
              sx={{
                fontSize: "36px",
                color: "#f39c12",
                fontWeight: "bold",
                lineHeight: 1,
              }}
            >
              !
            </Typography>
          </Box>
        </Box>
        {/* 
        <DialogTitle
          sx={{ fontSize: "28px", fontWeight: 600, color: "#333", mb: 2, p: 0 }}
        >
          ⚠️Attention
        </DialogTitle> */}

        <DialogContent sx={{ p: 0, mb: 4 }}>
          <Typography sx={{ color: "#666", fontWeight: 500, fontSize: "16px" }}>
            Please note that submitting this introduction, you will add them as
            a New Friend in your district.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 0, gap: 2, justifyContent: "center" }}>
          <Button
            variant="contained"
            label="Proceed"
            onClick={() => {
              setOpenDialog(false);
              if (pendingValues) postData(pendingValues);
            }}
            sx={{
              backgroundColor: "#28a745",
              color: "white",
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 500,
              padding: "12px 24px",
              minWidth: "120px",
              "&:hover": {
                backgroundColor: "#218838",
              },
            }}
          />
          <Button
            variant="contained"
            label="Cancel"
            onClick={() => {
              setOpenDialog(false);
              onBack();
            }}
            sx={{
              backgroundColor: "#dc3545",
              color: "white",
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 500,
              padding: "12px 24px",
              minWidth: "100px",
              "&:hover": {
                backgroundColor: "#c82333",
              },
            }}
          />
        </DialogActions>
      </Dialog>

      <Toast
        open={!!showToast}
        severity={showToast?.severity || ""}
        message={showToast?.message || ""}
        handleClose={() => {
          setShowToast(null);
        }}
      />
    </>
  );
}

export default ChangeToNewFriend;
